/**
 * FILE-OVERVIEW:
 * - 役割: 会議資料（日ごとのテーマ一覧）を種別ごとに振り分ける。
 * - 主な連携: components/MeetingSheet/*, components/Memo/index.tsx（種別の並び）
 * - 本番運用上の注意: 日付の判定はメモ画面のカレンダーと同じ dateKey を使う。
 *   ずらすと画面と配布資料でテーマが食い違う。
 */

import type { ThemeGroup } from '../services/s3Service';

/** メモ画面の絞り込みと同じ種別の並び。ここに無い種別は「その他」にまとめる */
export const MEMO_NEWS_GENRES = ['社会', '政治', '経済', '地域', '運動', '文化', '天鐘', '後日掲載'];
export const OTHER_NEWS_GENRE_LABEL = 'その他';

export interface MeetingSheetTheme {
  memoId: string;
  themeName: string;
}

export interface MeetingSheetSection {
  genre: string;
  themes: MeetingSheetTheme[];
}

const GENRE_ORDER = [...MEMO_NEWS_GENRES, OTHER_NEWS_GENRE_LABEL];

// カレンダーと同じく作成日時の新しい順。日時の書式が混在するため数字だけで比べる
const sortKey = (value: unknown): string => String(value || '').replace(/[^\d]/g, '').padEnd(14, '0');

/**
 * 指定日のテーマを種別ごとにまとめる（右側の選択一覧用）。テーマの無い種別は含めない。
 *
 * @param {ThemeGroup[]} themes groupContentsByTheme の結果
 * @param {string} dateKey YYYY-MM-DD
 */
export const listDayThemesByGenre = (themes: ThemeGroup[], dateKey: string): MeetingSheetSection[] => {
  const byGenre: Record<string, ThemeGroup[]> = {};
  themes
    .filter((theme) => theme.dateKey === dateKey)
    .forEach((theme) => {
      const genre = MEMO_NEWS_GENRES.includes(theme.newsGenre) ? theme.newsGenre : OTHER_NEWS_GENRE_LABEL;
      (byGenre[genre] ||= []).push(theme);
    });

  return GENRE_ORDER
    .filter((genre) => byGenre[genre]?.length)
    .map((genre) => ({
      genre,
      themes: byGenre[genre]!
        .sort((a, b) => {
          const diff = sortKey(b.firstCreatedDate).localeCompare(sortKey(a.firstCreatedDate));
          return diff !== 0 ? diff : String(a.memoId || '').localeCompare(String(b.memoId || ''));
        })
        .map((theme) => ({ memoId: theme.memoId, themeName: theme.themeName || '（テーマ名なし）' })),
    }));
};

/**
 * 選んだテーマだけで用紙の中身を作る。種別の並びは固定、種別内は選んだ順。
 * その日の一覧に無いID（テーマが消えた・別日のもの）は捨てる。
 */
export const buildSelectedSheet = (daySections: MeetingSheetSection[], selectedIds: string[]): MeetingSheetSection[] => {
  const order = new Map(selectedIds.map((id, i) => [id, i]));
  return daySections
    .map((section) => ({
      genre: section.genre,
      themes: section.themes
        .filter((theme) => order.has(theme.memoId))
        .sort((a, b) => order.get(a.memoId)! - order.get(b.memoId)!),
    }))
    .filter((section) => section.themes.length > 0);
};

/** Date をローカル時刻の YYYY-MM-DD にする（toISOString はUTCになり日付がずれる） */
export const toDateKey = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** YYYY-MM-DD に日数を足す */
export const shiftDateKey = (dateKey: string, days: number): string => {
  const [y, m, d] = dateKey.split('-').map(Number);
  return toDateKey(new Date(y!, m! - 1, d! + days));
};

export type PaperSize = 'A4' | 'A3';
export type PaperOrientation = 'portrait' | 'landscape';

export interface PaperLayout {
  widthMm: number;
  heightMm: number;
  /** 段数。1日70〜100件・テーマ名平均20字（2026-10実績）を1枚に収める前提で決めている */
  columns: number;
}

const PAPER_MM: Record<PaperSize, { short: number; long: number }> = {
  A4: { short: 210, long: 297 },
  A3: { short: 297, long: 420 },
};

const COLUMNS: Record<PaperSize, Record<PaperOrientation, number>> = {
  A4: { portrait: 2, landscape: 3 },
  A3: { portrait: 3, landscape: 4 },
};

/** 用紙の寸法と段数。保存値が壊れていても A4縦 に倒す */
export const getPaperLayout = (size: PaperSize, orientation: PaperOrientation): PaperLayout => {
  const paper = PAPER_MM[size] || PAPER_MM.A4;
  const isLandscape = orientation === 'landscape';
  return {
    widthMm: isLandscape ? paper.long : paper.short,
    heightMm: isLandscape ? paper.short : paper.long,
    columns: (COLUMNS[size] || COLUMNS.A4)[isLandscape ? 'landscape' : 'portrait'],
  };
};

/**
 * 選んだ順のIDの並びで、id を targetId の前（after=true なら後ろ）へ動かす。
 * 用紙の種別内の並びは選んだ順で決まるため、これで用紙上の並び替えになる。
 */
export const moveSelectedId = (ids: string[], id: string, targetId: string, after = false): string[] => {
  if (id === targetId || !ids.includes(id) || !ids.includes(targetId)) return ids;
  const rest = ids.filter((x) => x !== id);
  const index = rest.indexOf(targetId) + (after ? 1 : 0);
  return [...rest.slice(0, index), id, ...rest.slice(index)];
};
